'use client';

import { featureFlagService } from '@/@feature-flags/services';
import type {
    CreateFeatureFlagRequest,
    CreateOverrideRequest,
    FeatureFlagListResponse,
    UpdateFeatureFlagRequest,
} from '@/@feature-flags/types/api';
import {
    FEATURE_FLAGS_DEFAULTS,
    FEATURE_FLAGS_QUERY_CONFIG,
    FEATURE_FLAGS_QUERY_KEYS,
} from '@/@feature-flags/types/constants';
import type { FeatureFlag } from '@/@feature-flags/types/entities';
import { tracking } from '@/lib/features/tracking';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

const TOAST_OPTIONS = { position: 'top-right' } as const;

function clampFeatureFlagListPage(currentPage: number, total: number, pageLimit: number): number {
    const lim = pageLimit > 0 ? pageLimit : 1;
    const totalPages = Math.max(1, Math.ceil(total / lim));
    return Math.min(Math.max(1, currentPage), totalPages);
}

export function useFeatureFlagAdmin() {
    const queryClient = useQueryClient();
    const [page, setPage] = useState<number>(FEATURE_FLAGS_DEFAULTS.PAGE);
    const [limit] = useState<number>(FEATURE_FLAGS_DEFAULTS.LIMIT);
    const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null);

    const {
        data: listData,
        isLoading: isLoadingList,
        error: listError,
    } = useQuery({
        queryKey: FEATURE_FLAGS_QUERY_KEYS.list(page, limit),
        queryFn: () => featureFlagService.list(page, limit),
        placeholderData: keepPreviousData,
        staleTime: FEATURE_FLAGS_QUERY_CONFIG.STALE_TIME,
        gcTime: FEATURE_FLAGS_QUERY_CONFIG.GC_TIME,
    });

    const {
        data: allKeys,
        isLoading: isLoadingKeys,
        isError: isKeysError,
    } = useQuery({
        queryKey: FEATURE_FLAGS_QUERY_KEYS.keys,
        queryFn: () => featureFlagService.listAllKeys(),
        staleTime: FEATURE_FLAGS_QUERY_CONFIG.STALE_TIME,
        gcTime: FEATURE_FLAGS_QUERY_CONFIG.GC_TIME,
    });

    const {
        data: flagDetail,
        isLoading: isLoadingDetail,
        error: detailError,
    } = useQuery({
        queryKey: FEATURE_FLAGS_QUERY_KEYS.detail(selectedFlag?.key ?? ''),
        queryFn: () => featureFlagService.getByKey(selectedFlag!.key),
        staleTime: FEATURE_FLAGS_QUERY_CONFIG.STALE_TIME,
        gcTime: FEATURE_FLAGS_QUERY_CONFIG.GC_TIME,
        enabled: !!selectedFlag?.key,
    });

    useEffect(() => {
        if (!listData) return;
        const pageLimit = listData.limit > 0 ? listData.limit : limit;
        const nextPage = clampFeatureFlagListPage(page, listData.total, pageLimit);
        if (nextPage !== page) {
            setPage(nextPage);
        }
    }, [listData, limit, page]);

    const invalidateAll = useCallback(async () => {
        featureFlagService.clearCache();
        await queryClient.invalidateQueries({ queryKey: FEATURE_FLAGS_QUERY_KEYS.all });
        setPage((p) => {
            const data = queryClient.getQueryData<FeatureFlagListResponse>(
                FEATURE_FLAGS_QUERY_KEYS.list(p, limit),
            );
            if (!data) return p;
            const pageLimit = data.limit > 0 ? data.limit : limit;
            return clampFeatureFlagListPage(p, data.total, pageLimit);
        });
    }, [queryClient, limit]);

    const createMutation = useMutation({
        mutationFn: (payload: CreateFeatureFlagRequest) => featureFlagService.create(payload),
        onSuccess: async (data) => {
            toast.success(`Feature flag "${data.key}" created`, TOAST_OPTIONS);
            await invalidateAll();
        },
        onError: (error: Error) => {
            toast.error(`Failed to create flag: ${error.message}`, TOAST_OPTIONS);
            tracking.errorWithSource(error, 'useFeatureFlagAdmin.create');
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ key, payload }: { key: string; payload: UpdateFeatureFlagRequest }) =>
            featureFlagService.update(key, payload),
        onSuccess: async (data) => {
            toast.success(`Feature flag "${data.key}" updated`, TOAST_OPTIONS);
            await invalidateAll();
        },
        onError: (error: Error) => {
            toast.error(`Failed to update flag: ${error.message}`, TOAST_OPTIONS);
            tracking.errorWithSource(error, 'useFeatureFlagAdmin.update');
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (key: string) => featureFlagService.delete(key),
        onSuccess: async (_data, key) => {
            toast.success(`Feature flag "${key}" deleted`, TOAST_OPTIONS);
            setSelectedFlag(null);
            await invalidateAll();
        },
        onError: (error: Error) => {
            toast.error(`Failed to delete flag: ${error.message}`, TOAST_OPTIONS);
            tracking.errorWithSource(error, 'useFeatureFlagAdmin.delete');
        },
    });

    const addOverrideMutation = useMutation({
        mutationFn: ({ key, payload }: { key: string; payload: CreateOverrideRequest }) =>
            featureFlagService.addOverride(key, payload),
        onSuccess: async () => {
            toast.success('Override added', TOAST_OPTIONS);
            await invalidateAll();
        },
        onError: (error: Error) => {
            toast.error(`Failed to add override: ${error.message}`, TOAST_OPTIONS);
            tracking.errorWithSource(error, 'useFeatureFlagAdmin.addOverride');
        },
    });

    const removeOverrideMutation = useMutation({
        mutationFn: ({ key, overrideId }: { key: string; overrideId: number }) =>
            featureFlagService.removeOverride(key, overrideId),
        onSuccess: async () => {
            toast.success('Override removed', TOAST_OPTIONS);
            await invalidateAll();
        },
        onError: (error: Error) => {
            toast.error(`Failed to remove override: ${error.message}`, TOAST_OPTIONS);
            tracking.errorWithSource(error, 'useFeatureFlagAdmin.removeOverride');
        },
    });

    return {
        flags: listData?.data ?? [],
        total: listData?.total ?? 0,
        page,
        limit,
        setPage,
        isLoadingList,
        listError: listError as Error | null,

        createdKeys: allKeys ? new Set(allKeys) : null,
        isLoadingKeys,
        isKeysError,

        selectedFlag,
        setSelectedFlag,
        flagDetail: flagDetail ?? null,
        isLoadingDetail,
        detailError: detailError as Error | null,

        createFlag: createMutation.mutateAsync,
        isCreating: createMutation.isPending,

        updateFlag: (key: string, payload: UpdateFeatureFlagRequest) =>
            updateMutation.mutateAsync({ key, payload }),
        isUpdating: updateMutation.isPending,

        deleteFlag: deleteMutation.mutateAsync,
        isDeleting: deleteMutation.isPending,

        addOverride: (key: string, payload: CreateOverrideRequest) =>
            addOverrideMutation.mutateAsync({ key, payload }),
        isAddingOverride: addOverrideMutation.isPending,

        removeOverride: (key: string, overrideId: number) =>
            removeOverrideMutation.mutateAsync({ key, overrideId }),
        isRemovingOverride: removeOverrideMutation.isPending,

        invalidateAll,
    };
}
